import { useEffect, useRef } from 'react';

interface FireflyParticlesProps {
  count?: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

interface Firefly {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  phase: number;
  speed: number;
}

export function FireflyParticles({ count = 30, containerRef }: FireflyParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const firefliesRef = useRef<Firefly[]>([]);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resize();

    // Initialize fireflies
    const w = container.getBoundingClientRect().width;
    const h = container.getBoundingClientRect().height;
    firefliesRef.current = Array.from({ length: count }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.3,
      vy: -Math.random() * 0.2 - 0.1,
      size: Math.random() * 2 + 1,
      opacity: Math.random() * 0.6 + 0.2,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.02 + 0.01,
    }));

    let time = 0;
    const animate = () => {
      const rect = container.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      time += 0.016;

      for (const f of firefliesRef.current) {
        f.x += f.vx + Math.sin(time * 0.5 + f.phase) * 0.2;
        f.y += f.vy + Math.cos(time * 0.3 + f.phase) * 0.15;

        // Wrap around
        if (f.x < -10) f.x = rect.width + 10;
        if (f.x > rect.width + 10) f.x = -10;
        if (f.y < -10) f.y = rect.height + 10;
        if (f.y > rect.height + 10) {
          f.y = rect.height + 10;
          f.vy = -Math.random() * 0.2 - 0.1;
        }

        const flicker = Math.sin(time * 2 + f.phase) * 0.3 + 0.7;
        const alpha = f.opacity * flicker;

        // Outer glow
        const gradient = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.size * 4);
        gradient.addColorStop(0, `rgba(247, 215, 116, ${alpha})`);
        gradient.addColorStop(0.4, `rgba(247, 215, 116, ${alpha * 0.3})`);
        gradient.addColorStop(1, 'rgba(247, 215, 116, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.size * 4, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = `rgba(255, 240, 180, ${alpha})`;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [count, containerRef]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-10"
      aria-hidden="true"
    />
  );
}
